import React, { useEffect, createRef, useRef, useState, useMemo } from "react";
import { useHistory } from "react-router";
import classNames from "classnames";
import { arrowUndoOutline } from "ionicons/icons";
import { IconFlagFilled, IconBolt } from "@tabler/icons-react";
import {
  IonBadge,
  IonContent,
  isPlatform,
  IonList,
  IonItem,
  IonLabel,
  IonItemOptions,
  IonItemOption,
  IonItemSliding,
  IonIcon,
  IonSpinner,
} from "@ionic/react";

import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage } from "react-intl";

// CSS styles
import "./styles.css";

// Components
import { Message, ToolCall } from "../../interfaces";
import MessageContent from "./message-content";
import InteractiveActions from "./interactive-actions";
import MessageReactions from "./message-reactions";
import MediaModal from "./media-modal";
import ActionsModal from "./message-actions-modal";
import ReplyContent from "../common/reply-content";
import MessageActions from "./message-actions";
import ActionDropdown from "./message-actions-dropdown";

// API
import ChatQualityApi from "../../api/chat-quality";
import { groupMessages } from "../../utils/contacts";

// Hooks
import { useSelectedContactData } from "../../hooks";

interface ItemSlidingCustomEvent extends CustomEvent {
  target: HTMLIonItemSlidingElement;
}

interface ILayoutProps {
  replyTo: (args: { message: Message }) => void;
  actions?: string[];
  mode?: String;
  scroll?: Number;
  scrollEscalations?: Number;
  solveEscalations?: Function;
  type?: "playground" | "conversation" | "web-chat" | "onboarding";
  isLoading?: boolean;
  actionClick?: Function;
  typingIndicator?: boolean;
  selectedId?: string;
}

type TimelineItem =
  | { kind: "message"; item: Message }
  | { kind: "toolCall"; item: ToolCall };

const isMobile = isPlatform("android") || isPlatform("ios");

/*
 *
 * Pending:
 * Why use ref when there is a scroll to message id (escalations)
 *
 */

const ChatDetail: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const history = useHistory();
  const contentRef = useRef<HTMLIonContentElement | null>(null);

  const myRef = useRef<HTMLDivElement>(null);
  const {
    mode,
    scroll,
    scrollEscalations,
    solveEscalations,
    type = "conversation",
    replyTo,
    actionClick,
    actions = [],
    typingIndicator = false,
    selectedId,
  } = props;
  const { contact, messages, toolCalls } = useSelectedContactData(
    selectedId || "",
    type,
  );

  const messagesGroupedByDay = useMemo(
    () => groupMessages(messages, toolCalls),
    [messages],
  );

  const isEscalated = contact.escalated;
  const isLoading = props.isLoading;

  const [isOpen, setIsOpen] = useState(false);

  const [mediaMessage, setMediaMessage] = useState<Message | null>(null);

  // Actions drop down
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [dropdown, setDropdown] = useState({ visible: false, x: 0, y: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  //Actions modal
  const [isActionOpen, setIsActionOpen] = useState(false);
  const [actionedMessage, setActionedMessage] = useState<Message | null>(null);
  const [action, setAction] = useState<string>("");

  const slideTimeout = useRef<NodeJS.Timeout | null>(null);

  const prevId = useRef<string | undefined>(undefined);
  const prevCount = useRef<number>(0);

  const scrollToBottom = (duration: number) => {
    requestAnimationFrame(() => {
      contentRef.current?.scrollToBottom(duration || 0);
    });
  };
  useEffect(() => {
    if (messages.length === 0 || !contentRef.current) {
      return; // No messages to scroll
    }

    const isNewContact = !!contact.id && prevId.current !== contact.id;
    if (typingIndicator) {
      requestAnimationFrame(() => {
        contentRef.current?.scrollToBottom(300);
      });
    }

    const isNewMessage =
      !isNewContact &&
      (messages.length > prevCount.current ||
        (messages.length === 1 && prevCount.current === 0));

    /*
    console.log(
      `scroll: selectedId=${selectedId}, isNewContact=${isNewContact}, isNewMessage=${isNewMessage}, messages.length=${messages.length}, prevCount=${prevCount.current}`
    );
    */

    if (isNewContact) {
      requestAnimationFrame(() => {
        contentRef.current?.scrollToBottom(0);
      });
    } else if (isNewMessage) {
      requestAnimationFrame(() => {
        contentRef.current?.scrollToBottom(300);
      });
    }
    prevId.current = contact.id;
    prevCount.current = messages.length;
  }, [contact.id, messagesGroupedByDay, typingIndicator]);

  useEffect(() => {
    if (scroll) {
      contentRef.current?.scrollToBottom(300);
    }
  }, [scroll]);

  useEffect(() => {
    if (scrollEscalations) {
      myRef?.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [scrollEscalations]);

  const openMedia = (message: Message) => {
    setMediaMessage(message);
    setIsOpen(true);
  };

  const scrollToMessage = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ block: "center", behavior: "smooth" });
      element.classList.add("highlight");
      setTimeout(() => {
        element.classList.remove("highlight");
      }, 1000);
    }
  };

  const handleSlide = async (e: ItemSlidingCustomEvent, message: Message) => {
    if (slideTimeout.current) {
      clearTimeout(slideTimeout.current);
    }
    slideTimeout.current = setTimeout(async () => {
      const finalRatio = await e.target.getSlidingRatio();
      if (finalRatio <= -1) {
        await e.target.closeOpened();
        replyTo({ message });
      }
    }, 300);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdown.visible &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdown((prev) => ({ ...prev, visible: false }));
        setSelectedMessage(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef, dropdown.visible]);

  const handleShowActions = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdown({
      visible: true,
      x: rect.left,
      y: rect.bottom - 50,
    });
  };

  const handleAction = async ({
    message,
    action,
  }: {
    message: Message;
    action: string;
  }) => {
    setActionedMessage(message);
    setAction(action);

    const cloneMessage = async ({ message }: { message: Message }) => {
      const { data, err } = await ChatQualityApi.copyToPlayground({
        messageId: message.id,
      });
      if (data) {
        history.push(`/ai`);
      }
      if (err) {
        console.error("Error copying message:", err);
      }
    };

    switch (action) {
      case "reply":
        replyTo({ message });
        break;
      case "clone":
        await cloneMessage({ message });
        break;
      case "debug":
        setIsActionOpen(true);
        break;
      case "flag":
        setIsActionOpen(true);
        break;
      default:
        console.log("not defined action:", action);
    }
  };

  const Thinking = (
    <IonItem
      lines="none"
      className={classNames("", {
        right: true,
      })}
    >
      <IonLabel>
        <div
          className={classNames("chat-wrapper", {
            right: true,
          })}
        >
          <div className={classNames("chat-holder")}>
            <div className="chat-item spinner-holder">
              <IonSpinner name="dots" />
            </div>
          </div>
        </div>
      </IonLabel>
    </IonItem>
  );

  return (
    <IonContent ref={contentRef} scrollY={dropdown.visible ? false : true}>
      {isLoading && (
        <div className="loadingMessages fs-4 sticky-top flex justify-content-center align-items-center gap-2">
          <IonSpinner name="crescent" />
          <p className="mb-0">
            <FormattedMessage id="contactDetail.loadingChat" />
          </p>
        </div>
      )}
      <IonList
        className={classNames(
          "chat-detail-list flex flex-column justify-content-end",
          {
            playground: type === "playground",
            "web-chat": type === "web-chat",
          },
        )}
      >
        {Object.entries(messagesGroupedByDay ?? {}).map(([key, grouped]) => (
          <React.Fragment key={key}>
            <IonItem lines="none">
              <IonLabel className="time-badge">
                <IonBadge color="light">{key}</IonBadge>
              </IonLabel>
            </IonItem>

            {grouped.map((row: TimelineItem) => {
              const { kind } = row;

              if (kind === "toolCall" && type === "playground") {
                const tool = row.item;
                return (
                  <IonItem lines="none" id={tool.id}>
                    <IonLabel>
                      <div className="d-flex  gap-2">
                        <OverlayTrigger
                          key={action}
                          placement="bottom"
                          delay={{ show: 250, hide: 400 }}
                          overlay={
                            <Tooltip className="visito-tooltip">
                              {tool.content}
                            </Tooltip>
                          }
                        >
                          <div className="badge text-bg-light d-flex align-items-center gap-1">
                            <div key={action}>
                              <IconBolt size={14}></IconBolt> {tool.name}
                            </div>
                          </div>
                        </OverlayTrigger>
                        <span className="fs-2 color-step-550">
                          {tool.arguments}
                        </span>
                      </div>
                    </IonLabel>
                  </IonItem>
                );
              }
              if (kind === "message") {
                const message = row.item;
                const isActive = selectedMessage?.id === message.id;
                const isHovered = hoveredMessageId === message.id;
                const shouldShowActions =
                  isActive || (!selectedMessage && isHovered);
                const renderItemContent = () => (
                  <IonItem
                    lines="none"
                    id={message.id}
                    className={classNames("", {
                      right: message.right,
                      escalated:
                        message.escalated === true &&
                        ["conversation", "playground"].includes(type),
                    })}
                  >
                    <IonLabel>
                      <div
                        {...(message.escalated ? { ref: myRef } : {})}
                        className={classNames("chat-wrapper", {
                          right: message.right,
                          escalated:
                            message.escalated === true &&
                            ["conversation", "playground"].includes(type),
                        })}
                      >
                        <div
                          className={classNames("chat-holder", {
                            interactive: message.interactive.flag === true,
                          })}
                          onMouseEnter={() => {
                            setHoveredMessageId(message?.id || null);
                          }}
                          onMouseLeave={() => {
                            setHoveredMessageId(null);
                          }}
                        >
                          {!isMobile &&
                            type !== "web-chat" &&
                            shouldShowActions && (
                              <MessageActions
                                showActions={(e) => {
                                  handleShowActions(e);
                                  setSelectedMessage(message);
                                }}
                              />
                            )}
                          <div className="chat-item">
                            <ReplyContent
                              message={message.replyTo}
                              right={message.right}
                              onAfterClick={(id: string) => scrollToMessage(id)}
                            />
                            <MessageContent
                              message={message}
                              mode={mode}
                              type={type}
                              openMedia={openMedia}
                              scrollToBottom={scrollToBottom}
                            />
                          </div>
                          <MessageReactions message={message} />
                          <InteractiveActions
                            message={message}
                            type={type}
                            actionClick={actionClick}
                          />
                          {message.error && (
                            <div className="chat-item-error color-step-500">
                              {message.error}
                            </div>
                          )}
                        </div>
                      </div>
                    </IonLabel>
                    {message.flag && ["conversation"].includes(type) && (
                      <div
                        slot="end"
                        className="chat-item-flag"
                        onClick={() => {
                          setIsActionOpen(true);
                          setAction("flag");
                          setActionedMessage(message);
                        }}
                      >
                        <div>
                          <IconFlagFilled size={20} />
                        </div>
                      </div>
                    )}
                  </IonItem>
                );
                return isMobile ? (
                  <IonItemSliding
                    key={message.id || "new"}
                    onIonDrag={(e: ItemSlidingCustomEvent) =>
                      handleSlide(e, message)
                    }
                  >
                    <IonItemOptions side="start">
                      <IonItemOption color="light">
                        <IonIcon slot="end" icon={arrowUndoOutline}></IonIcon>
                      </IonItemOption>
                    </IonItemOptions>

                    {renderItemContent()}
                  </IonItemSliding>
                ) : (
                  <React.Fragment key={message.id || "new"}>
                    {renderItemContent()}
                  </React.Fragment>
                );
              }
            })}
          </React.Fragment>
        ))}
        {typingIndicator ? Thinking : null}
        {mode !== "desktop" && isEscalated && solveEscalations ? (
          <div className="escalation-button-holder">
            <button
              onClick={() => solveEscalations(contact.id)}
              className="btn btn-visito-primary-border"
            >
              <FormattedMessage id="esc.solve" />
            </button>
          </div>
        ) : null}
      </IonList>

      <MediaModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        message={mediaMessage}
      />

      {action && actionedMessage && (
        <ActionsModal
          isOpen={isActionOpen}
          setIsOpen={(bool: boolean) => {
            setIsActionOpen(bool);
            setActionedMessage(null);
            setAction("");
          }}
          action={action}
          message={actionedMessage}
          messages={messages}
        />
      )}

      {!isMobile && (
        <ActionDropdown
          ref={dropdownRef}
          actions={actions}
          dropdown={dropdown}
          selectedMessage={selectedMessage}
          onAction={({ action }: { action: string }) => {
            if (selectedMessage) {
              handleAction({
                message: selectedMessage,
                action,
              });
            }
          }}
          onClose={() => {
            setSelectedMessage(null);
            setDropdown((v) => ({ ...v, visible: false }));
          }}
        />
      )}
    </IonContent>
  );
};

export default ChatDetail;
